/** What kind of failure stopped a composed run, named from the stage it stopped
 * in and its saved message. Each kind gets its own recovery; the message stays
 * saved for admins, and creators see only the plain film line. */
export const COMPOSED_FAILURE_KINDS = ['temporary', 'plan-text', 'plan-other', 'rule', 'template', 'assembly', 'build', 'limit'] as const;
export type ComposedFailureKindV1 = typeof COMPOSED_FAILURE_KINDS[number];

/** Waiting can fix these: the provider or the builder didn't answer. An auth or
 * bad-request refusal from a provider isn't here, since it fails the same later.
 * Nor is a stage that ran out its whole time bound: it may have spent every call,
 * and a slow stage is as slow later. */
const TEMPORARY = [
  /\[[a-z]+\] .*\baborted\b/i,
  /\[[a-z]+\] .*\(status (?:408|409|429|5\d\d)\)/,
  /\[[a-z]+\] .*(?:overloaded|rate.?limit|usage limit|quota|credit balance|timed? ?out|fetch failed|socket hang up|ECONNRESET|ETIMEDOUT)/i,
  /service unreachable$/,
  /builder responded 5\d\d$/,
];

/** A text diagnostic ("missing[1].sketch length") or the closed parser's rendered
 * length issue (`schemaIssueText`: "explanation: too long (actual 812 chars, limit 600 chars)"). */
const PLAN_TEXT_FIELD = /^(?:explanation|missing\[\d+\]\.(?:need|sketch))(?: length|: (?:too long|empty) \(actual \d+ chars, limit [\d.]+ chars\))$/;

/** True when the plan's only defect was text over a length limit, in either form
 * the failure summary saves: one parser line, or the JSON list of diagnostics. */
function onlyPlanTextLength(message: string): boolean {
  const detail = message.replace(/^compose plan failed: compose plan reply invalid after retry: /, '');
  if (detail === message) return false;
  if (detail.startsWith('[')) {
    try {
      const list = JSON.parse(detail) as Array<{ code?: unknown }>;
      return list.length > 0 && list.every(item => item.code === 'length');
    } catch { return false; }
  }
  return detail.split('; ').every(part => PLAN_TEXT_FIELD.test(part));
}

/** How a fresh Create's refusal to start another attempt at the idea begins. */
export const COMPOSED_IDEA_LIMIT_MESSAGE = 'Idea limit reached';

export function composedFailureKindV1(stage: string, message: string): ComposedFailureKindV1 {
  if (message.startsWith(COMPOSED_IDEA_LIMIT_MESSAGE)) return 'limit';
  if (TEMPORARY.some(pattern => pattern.test(message))) return 'temporary';
  if (stage === 'plan') return onlyPlanTextLength(message) ? 'plan-text' : 'plan-other';
  if (stage === 'synthesis') return 'assembly';
  if (stage === 'build') return 'build';
  return message.startsWith('composed template') ? 'template' : 'rule';
}

/** Waits before the quick automatic retries of a temporary failure, in seconds. A
 * lost stage gets only these. */
export const COMPOSED_TEMPORARY_RETRY_DELAYS_SECONDS = [60, 300, 900] as const;

/** After the quick retries an outage waits (Decision 175): one try an hour for a
 * day. Then the run fails, and the failure stays named temporary. */
export const COMPOSED_OUTAGE_RETRY_SECONDS = 3600;
export const COMPOSED_OUTAGE_RETRIES = 24;
/** An hourly outage retry repeats the failed stage's call, which a provider may
 * bill. Once the run's recorded spend since its first retry reaches this, it
 * stops waiting and fails: the creator gets Start again (Decision 208). */
export const COMPOSED_OUTAGE_BUDGET_USD = 5;

/** The wait before temporary retry number `count` (0-based), or null once spent. */
export function composedTemporaryRetryDelayV1(count: number): number | null {
  if (count < COMPOSED_TEMPORARY_RETRY_DELAYS_SECONDS.length) return COMPOSED_TEMPORARY_RETRY_DELAYS_SECONDS[count]!;
  return count < COMPOSED_TEMPORARY_RETRY_DELAYS_SECONDS.length + COMPOSED_OUTAGE_RETRIES ? COMPOSED_OUTAGE_RETRY_SECONDS : null;
}

/** True while a run waits out an outage: its quick retries are spent and an
 * hourly one is due. Create shows the waiting screen and the Studio badge. */
export function composedOutageWaitingV1(run: { status: string; temporaryRetry?: { count: number } }): boolean {
  return run.status !== 'failed' && (run.temporaryRetry?.count ?? 0) > COMPOSED_TEMPORARY_RETRY_DELAYS_SECONDS.length;
}

/** The film's line for a stopped run. */
export const COMPOSED_FAILURE_FILM_LINE = 'This step didn’t work out. Your finished pieces are saved.';
export const COMPOSED_TEMPORARY_FILM_LINE = 'We hit a busy moment. Your finished pieces are saved.';

export function composedFailureFilmLineV1(kind: ComposedFailureKindV1): string {
  return kind === 'temporary' ? COMPOSED_TEMPORARY_FILM_LINE : COMPOSED_FAILURE_FILM_LINE;
}
